import React from 'react'
import { useNavigate } from 'react-router-dom'
import { theme } from '../../../../core/design-system/theme'
import { useBookingContext, BookingStepShell } from '../BookingFlowView'

const SERVICE_FEE = 3

function SectionLabel({ children }) {
  return (
    <h2
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: theme.colors.textSecondary,
        fontFamily: 'var(--font-body)',
        margin: '0 0 10px',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </h2>
  )
}

function Row({ label, value, noBorder }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
        padding: '9px 0',
        borderBottom: noBorder ? 'none' : `1px solid ${theme.colors.border}`,
      }}
    >
      <span
        style={{
          fontSize: 14,
          color: theme.colors.textSecondary,
          fontFamily: 'var(--font-body)',
          flexShrink: 0,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          color: theme.colors.textPrimary,
          fontFamily: 'var(--font-body)',
          textAlign: 'right',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </span>
    </div>
  )
}

function formatDateTime(isoStr) {
  if (!isoStr) return '—'
  const d = new Date(isoStr)
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
}

function formatChildren(children) {
  if (!children?.length) return '—'
  return children.map((c) => `${c.fullName} (${c.ageYears}y)`).join(', ')
}

export default function BookingStep5View() {
  const { viewModel } = useBookingContext()
  const navigate = useNavigate()

  const { draft } = viewModel
  const hours = draft.schedule?.durationHours ?? 0
  const hourlyRate = draft.selectedNonna?.hourlyRate ?? 0
  const subtotal = hourlyRate * hours
  const total = subtotal + SERVICE_FEE

  return (
    <BookingStepShell
      step={5}
      title="Review your booking"
      onBack={() => navigate(-1)}
      onContinue={() => navigate('/book/pay')}
    >
      <div
        style={{
          background: theme.colors.card,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.layout.cardRadius,
          padding: 20,
        }}
      >
        <SectionLabel>Booking Details</SectionLabel>
        <Row label="Parent" value={draft.parentInfo?.fullName ?? '—'} />
        <Row label="Email" value={draft.parentInfo?.email ?? '—'} />
        <Row label="Children" value={formatChildren(draft.children)} />
        <Row label="Date & time" value={formatDateTime(draft.schedule?.scheduledStart)} />
        <Row
          label="Duration"
          value={hours === 1 ? '1 hour' : `${hours} hours`}
        />
        <Row label="Location" value={draft.schedule?.locationLabel ?? '—'} />
        <Row label="Nonna" value={draft.selectedNonna?.name ?? '—'} noBorder />

        <div
          style={{
            marginTop: 16,
            borderTop: `1px solid ${theme.colors.borderStrong}`,
            paddingTop: 14,
          }}
        >
          <SectionLabel>Pricing</SectionLabel>
          <Row
            label={`Subtotal (${hours}h × $${hourlyRate}/hr)`}
            value={`$${subtotal.toFixed(2)}`}
          />
          <Row label="Service fee" value="$3.00" />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 12,
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: theme.colors.textPrimary,
                fontFamily: 'var(--font-body)',
              }}
            >
              Total
            </span>
            <span
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: theme.colors.terracotta,
                fontFamily: 'var(--font-body)',
              }}
            >
              ${total.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </BookingStepShell>
  )
}
